// ---------------------------------------------------------------------------
// A bundle's adjustments to the UX template it deploys with.
//
// The bundle stores a DELTA, never a copy. That is what makes the reference live: everything the
// bundle does not override keeps following the template, so editing the template changes what every
// bundle pointing at it deploys. Storing the whole config instead would make every setting an
// override and a template edit would reach nothing — the feature would look like it worked and
// quietly not.
//
// The wizard's apply mode hands back a COMPLETE config (it derives the fields no control drives),
// so the delta has to be recovered by comparing it with the template it was opened on.
// ---------------------------------------------------------------------------

type Json = Record<string, any>

const isPlainObject = (v: any): v is Json =>
  v !== null && typeof v === 'object' && !Array.isArray(v)

/**
 * What `next` says that `base` does not.
 *
 * Nested objects recurse, so overriding one field inside `welcome` does not freeze its siblings.
 * Arrays and scalars compare whole: half an array is not a setting anyone authored.
 *
 * A key `base` has and `next` does not is NOT recorded. The merge is additive on the server, so a
 * deletion cannot be expressed — and the wizard always emits a complete config, so a missing key
 * means "the wizard does not write this one", not "the author removed it".
 */
export function overridesFrom(base: Json | null | undefined, next: Json | null | undefined): Json {
  const b = base ?? {}
  const n = next ?? {}
  const out: Json = {}

  for (const [key, value] of Object.entries(n)) {
    const before = b[key]
    if (isPlainObject(value) && isPlainObject(before)) {
      const nested = overridesFrom(before, value)
      if (Object.keys(nested).length > 0) out[key] = nested
    } else if (JSON.stringify(value) !== JSON.stringify(before)) {
      out[key] = value
    }
  }
  return out
}

/** The template's settings with a bundle's adjustments applied — what the machine will see. */
export function mergedConfig(base: Json | null | undefined, overrides: Json | null | undefined): Json {
  const out: Json = { ...(base ?? {}) }
  for (const [key, value] of Object.entries(overrides ?? {})) {
    out[key] = isPlainObject(value) && isPlainObject(out[key])
      ? mergedConfig(out[key], value)
      : value
  }
  return out
}

/** How many settings a bundle has changed, for a caller that wants to say so without listing them. */
export function overrideCount(overrides: Json | null | undefined): number {
  let n = 0
  for (const value of Object.values(overrides ?? {})) {
    n += isPlainObject(value) ? overrideCount(value) : 1
  }
  return n
}
